import { SessionExpiredError } from "./questionsHistoryApi";
import { SITE_SERVER_URL } from "@/shared/config/player";
// Пакеты (.com): у человека в режиме пакетов кошелёк — вид site_server, а не
// quota.json (features/supPlan; TECH_PLAN §10.2).
import { browserTimeZone, normalizePlanWallet, type PlanWallet } from "@/features/supPlan/api/supPlanApi";
import { isPlansModeNow, loadPlanModeQuick, setPlanWallet } from "@/features/supPlan/model/planStore";
import { planQuota } from "@/features/supPlan/model/planQuota";

/**
 * Вид пакетов, если человек в этом режиме; иначе null. Сбой у известного «пакетного» (или
 * «режим неизвестен» от сервера) — ошибка. Тем, кто на этом устройстве ещё не был на
 * пакетах, сервер пакетов отвечает за 2,5 с (loadPlanModeQuick) — иначе их токены не ждут.
 */
const planWalletForQuota = async (visit = false): Promise<PlanWallet | null> => {
    const w = await loadPlanModeQuick(undefined, { visit });
    return w && w.mode === "plans" ? w : null;
};

export const PERSONAL_DATA_WORKER_URL =
    "https://personal-data-worker.torsunovoleggen.workers.dev";

export const QUOTA_PATH = "questions/quota.json";

/**
 * Bearer token for the personal-data worker (where the quota lives). Same
 * fallback order the rest of the INT account API uses — `auth_token` is a
 * mirror that goes stale, so it isn't checked first (see shared/api/otherScores).
 */
export const walletToken = (): string => {
    try {
        return (
            localStorage.getItem("personal_data_session_token") ||
            localStorage.getItem("auth_token") ||
            localStorage.getItem("r2_session_token") ||
            ""
        );
    } catch {
        return "";
    }
};

// ---------- v2 data model ----------

export interface AnswerPackage {
    id: string;
    count: number;
    initialCount: number;
    expiresAt: string; // ISO date
    source:
        | "purchase"
        | "share"
        | "club"
        | "welcome"
        | "refund"
        // One-time grant on the buyer's FIRST paid product of any kind. Written
        // server-side only (payments.py maybe_grant_purchase_bonus) behind an R2
        // marker, so unlike `welcome` it cannot be re-seeded from the client.
        | "purchase-bonus"
        // Tokens that came with a gifted bundle (features/gifts): credited to the
        // recipient server-side at claim time, like "purchase" otherwise.
        | "gift";
    createdAt: string;
}

// ---------- costs ----------
//
// One wallet meters every paid action. Running the dock's clip search is not
// charged by itself (a positive balance is the gate — see `canSearch` below);
// watching the clips it found is: CLIP_SEARCH_OPEN_COST once per query. The
// clip QUESTION («Моего вопроса нет — показать фрагменты») costs
// CLIP_QUESTION_COST, charged only when it found something.
export const PREBUILT_COST = 1;
export const OWN_QUESTION_COST = 2;
export const CLIP_QUESTION_COST = 1;
/** Opening clips from one clip-search list: charged once per query — any
 *  number of clips from that list, now or later, for this one token. */
export const CLIP_SEARCH_OPEN_COST = 1;
/** Live specialist question — sending one in /account/specialist-chat debits
 *  this many tokens from the same wallet (see SpecialistChatPage). */
export const SPECIALIST_COST = 3;
export const WELCOME_PACK_COUNT = 2;

export interface QuestionQuota {
    version: 2;
    packages: AnswerPackage[];
    viewed: string[]; // hashes — always free to re-view
    welcomeGranted?: boolean; // one-time welcome pack flag (RU parity)
}

// ---------- helpers ----------

function generateId(): string {
    return crypto.randomUUID?.() ?? Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function isExpired(pkg: AnswerPackage): boolean {
    return new Date(pkg.expiresAt) <= new Date();
}

/** Total remaining answers across all non-expired packages. */
export function getAvailableCount(quota: QuestionQuota): number {
    return quota.packages
        .filter((p) => !isExpired(p) && p.count > 0)
        .reduce((sum, p) => sum + p.count, 0);
}

/**
 * Clip search is free, but not to nobody: holding a positive balance is what
 * turns it on. A GATE, never a debit — no `consumeCredits` call, no query hash
 * in `viewed`, so running the same search a hundred times costs nothing and
 * leaves no trace in the quota file.
 *
 * Every fresh login already lands here with credits (`seedWelcomeIfNeeded`
 * grants 2), so a new user gets working search before spending anything.
 */
export function canSearch(quota: QuestionQuota): boolean {
    return getAvailableCount(quota) > 0;
}

/** Welcome, refund and the first-purchase bonus are not transferable via
 *  /share-links/create — none of them was paid for by this user. */
const NON_SHAREABLE_SOURCES = new Set<AnswerPackage["source"]>([
    "welcome",
    "refund",
    "purchase-bonus",
]);

/** Credits the user can send via "Поделиться" — excludes welcome + refund. */
export function getShareableCount(quota: QuestionQuota): number {
    let total = quota.packages
        .filter((p) =>
            !isExpired(p) &&
            p.count > 0 &&
            !NON_SHAREABLE_SOURCES.has(p.source),
        )
        .reduce((sum, p) => sum + p.count, 0);
    // #1062: defensive subtract if legacy v1→v2 lumped welcome as "purchase".
    if (
        quota.welcomeGranted &&
        !quota.packages.some((p) => p.source === "welcome")
    ) {
        total = Math.max(0, total - WELCOME_PACK_COUNT);
    }
    return total;
}

/** Earliest expiry among non-empty, non-expired packages (or null). */
export function getEarliestExpiry(quota: QuestionQuota): string | null {
    const active = quota.packages.filter((p) => !isExpired(p) && p.count > 0);
    if (active.length === 0) return null;
    return active.reduce(
        (earliest, p) => (p.expiresAt < earliest ? p.expiresAt : earliest),
        active[0].expiresAt,
    );
}

/**
 * Consume one answer from the oldest non-expired package.
 * Returns a new QuestionQuota with the hash added to `viewed`.
 * If the hash is already viewed, returns quota unchanged.
 */
export function consumeAnswer(
    quota: QuestionQuota,
    hash: string,
): QuestionQuota {
    if (quota.viewed.includes(hash)) return quota;

    const packages = quota.packages.map((p) => ({ ...p }));
    // consume from the oldest non-expired package with remaining count
    const target = packages.find((p) => !isExpired(p) && p.count > 0);
    if (target) {
        target.count -= 1;
    }

    return {
        version: 2,
        packages,
        viewed: [...quota.viewed, hash],
    };
}

/** Create a new purchase package. */
export function createPurchasePackage(
    count: number,
    daysValid: number,
): AnswerPackage {
    const now = new Date();
    const expires = new Date(now);
    expires.setDate(expires.getDate() + daysValid);
    return {
        id: generateId(),
        count,
        initialCount: count,
        expiresAt: expires.toISOString(),
        source: "purchase",
        createdAt: now.toISOString(),
    };
}

/** True when the quota has at least `cost` credits remaining. */
export function hasEnoughCredits(
    quota: QuestionQuota,
    cost: number,
): boolean {
    return getAvailableCount(quota) >= cost;
}

/**
 * Debit `cost` credits from the oldest non-expired packages. The `hash` is
 * added to the `viewed` set so the same answer doesn't re-charge later;
 * for prebuilt answers, a hash that's already in `viewed` is a free re-view.
 * Returns the new quota; caller is responsible for persisting.
 *
 * Signature mirrors RU `consumeCredits(quota, hash, cost, opts?)`.
 */
export function consumeCredits(
    quota: QuestionQuota,
    hash: string,
    cost: number,
    opts: { respectViewed?: boolean } = {},
): QuestionQuota {
    const respectViewed = opts.respectViewed !== false;
    if (
        respectViewed &&
        cost === PREBUILT_COST &&
        quota.viewed.includes(hash)
    ) {
        return quota;
    }

    const packages = quota.packages.map((p) => ({ ...p }));
    let remaining = cost;
    for (const p of packages) {
        if (remaining <= 0) break;
        if (isExpired(p) || p.count <= 0) continue;
        const take = Math.min(remaining, p.count);
        p.count -= take;
        remaining -= take;
    }

    const viewed = quota.viewed.includes(hash)
        ? quota.viewed
        : [...quota.viewed, hash];

    return { ...quota, version: 2, packages, viewed };
}

/**
 * Debit `cost` tokens from the oldest non-expired packages, oldest first.
 * Unlike `consumeCredits`, this touches no `viewed` hash — a specialist
 * question is a fresh charge every time, not a re-viewable answer. Returns
 * the new quota; caller persists via `saveQuestionQuota`.
 */
export function debitCredits(quota: QuestionQuota, cost: number): QuestionQuota {
    const packages = quota.packages.map((p) => ({ ...p }));
    let remaining = cost;
    for (const p of packages) {
        if (remaining <= 0) break;
        if (isExpired(p) || p.count <= 0) continue;
        const take = Math.min(remaining, p.count);
        p.count -= take;
        remaining -= take;
    }
    return { ...quota, version: 2, packages };
}

/** Create the 2-free-answers "welcome" package that a newly-logged-in user
 *  should start with. Valid 365 days. */
export function createWelcomePackage(): AnswerPackage {
    const now = new Date();
    const expires = new Date(now);
    expires.setDate(expires.getDate() + 365);
    return {
        id: "welcome-" + generateId(),
        count: 2,
        initialCount: 2,
        expiresAt: expires.toISOString(),
        source: "welcome",
        createdAt: now.toISOString(),
    };
}

/**
 * Synchronously decide whether to seed a 2-credit welcome pack: only when
 * welcomeGranted is falsy, there are no available credits, and the user
 * hasn't viewed any answers yet. Returns a mutated quota when seeded
 * (caller must persist via saveQuestionQuota); otherwise returns input.
 *
 * Matches RU parity: `seedWelcomeIfNeeded(quota)`.
 */
export function seedWelcomeIfNeeded(quota: QuestionQuota): QuestionQuota {
    if (quota.welcomeGranted) return quota;
    if (getAvailableCount(quota) > 0) return quota;
    if (quota.viewed.length > 0) return quota;
    return {
        ...quota,
        version: 2,
        packages: [createWelcomePackage(), ...quota.packages],
        welcomeGranted: true,
    };
}

// ---------- migration from v1 ----------

interface QuestionQuotaV1 {
    used?: number;
    limit?: number;
    viewed?: string[];
}

function migrateV1(old: QuestionQuotaV1): QuestionQuota {
    const used = old.used ?? 0;
    const limit = old.limit ?? 2;
    const remaining = Math.max(0, limit - used);

    const packages: AnswerPackage[] = [];
    if (remaining > 0) {
        packages.push({
            id: generateId(),
            count: remaining,
            initialCount: limit,
            expiresAt: "2099-12-31T23:59:59.999Z", // far-future for legacy
            source: "purchase",
            createdAt: new Date().toISOString(),
        });
    }

    return {
        version: 2,
        packages,
        viewed: old.viewed ?? [],
    };
}

// ---------- default (2 free answers for new users) ----------

const DEFAULT_QUOTA: QuestionQuota = {
    version: 2,
    packages: [
        {
            id: "default-free",
            count: 2,
            initialCount: 2,
            expiresAt: "2099-12-31T23:59:59.999Z",
            source: "club",
            createdAt: new Date().toISOString(),
        },
    ],
    viewed: [],
};

// ---------- fetch / save ----------

/** A v2 wallet file (from the worker or from site_server) in the page's shape. */
export function normalizeQuota(data: {
    packages?: AnswerPackage[] | null;
    viewed?: string[] | null;
    welcomeGranted?: boolean | null;
}): QuestionQuota {
    return {
        version: 2,
        packages: Array.isArray(data?.packages) ? data.packages : [],
        viewed: Array.isArray(data?.viewed) ? data.viewed : [],
        welcomeGranted: !!data?.welcomeGranted,
    };
}

/** quota.json как есть (только чтение); сбой сети — Error, а не исключение в пути. */
const readQuotaFile = (token: string): Promise<Response | Error> =>
    fetch(`${PERSONAL_DATA_WORKER_URL}/data/${QUOTA_PATH}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
    }).then(
        (r) => r,
        (e: unknown) => (e instanceof Error ? e : new Error(String(e))),
    );

/**
 * Кошелёк умного поиска. `visit` (по умолчанию — да: этот кошелёк читают страницы СУПа и
 * лекций) — у человека на пакетах это заход в поиск: ждущий месяц в подарок за покупку
 * начинается. Чату специалиста нужен только баланс — он передаёт `visit: false`.
 */
export async function fetchQuestionQuota(
    token: string,
    opts: { visit?: boolean } = {},
): Promise<QuestionQuota> {
    // Режим пакетов: quota.json не пишется — ни приветственных токенов, ни
    // переноса v1. Не прочитался вид — ошибка (страница переспросит), а не
    // «2 токена по умолчанию». Пока режим не известен, quota.json читается
    // параллельно (только чтение): у токенов кошелёк не ждёт сервер пакетов.
    const readQuota = () => readQuotaFile(token);
    const early = isPlansModeNow() ? null : readQuota();
    const plan = await planWalletForQuota(opts.visit !== false);
    if (plan) return planQuota(plan);
    try {
        const got = await (early ?? readQuota());
        if (got instanceof Error) throw got;
        const response = got;

        if (response.status === 401) throw new SessionExpiredError();
        if (!response.ok) {
            // First-time user: 404 → seed welcome pack so they see 2 free
            // credits on first login instead of «Токенов: 0».
            const seeded = seedWelcomeIfNeeded({
                ...DEFAULT_QUOTA,
                packages: [...DEFAULT_QUOTA.packages],
            });
            saveQuestionQuota(token, seeded).catch(() => {});
            return seeded;
        }

        const data = await response.json();

        // already v2
        if (data.version === 2) {
            const current = normalizeQuota(data);
            const seeded = seedWelcomeIfNeeded(current);
            if (seeded !== current) {
                saveQuestionQuota(token, seeded).catch(() => {});
            }
            return seeded;
        }

        // migrate v1 → v2, then consider welcome seed
        const migrated = migrateV1(data as QuestionQuotaV1);
        const seeded = seedWelcomeIfNeeded(migrated);
        // persist migration (and welcome seed if any) in background
        saveQuestionQuota(token, seeded).catch(() => {});
        return seeded;
    } catch (e) {
        if (e instanceof SessionExpiredError) throw e;
        return { ...DEFAULT_QUOTA, packages: [...DEFAULT_QUOTA.packages] };
    }
}

/**
 * Read-only token balance for pages that only display it. Unlike
 * `fetchQuestionQuota`, a network / server failure REJECTS instead of
 * masking as a default quota — the caller wants to show an error state.
 * A 404 (brand-new user, no quota file yet) resolves to 0, not an error.
 */
export async function fetchTokenBalance(token: string): Promise<number> {
    // Режим пакетов: токенов у человека нет — счётчик не рисуют (меню, курс).
    // Режим и quota.json спрашиваются параллельно: у человека на токенах счётчик ждёт
    // сервер пакетов не дольше быстрой проверки (2,5 с), а не всё чтение.
    const early = isPlansModeNow() ? null : readQuotaFile(token);
    if (await planWalletForQuota()) throw new Error("plans_mode");
    const got = await (early ?? readQuotaFile(token));
    if (got instanceof Error) throw got;
    const response = got;
    if (response.status === 404) return 0;
    if (!response.ok) throw new Error(`quota ${response.status}`);
    const data = await response.json();
    const quota: QuestionQuota =
        data?.version === 2
            ? { version: 2, packages: data.packages ?? [], viewed: [] }
            : migrateV1(data as QuestionQuotaV1);
    return getAvailableCount(quota);
}

/**
 * «Кошелёк изменился».
 *
 * Число токенов живёт в двух местах разных деревьев: в шапке умного поиска
 * (своё состояние страницы) и в меню кабинета. Меню перечитывало кошелёк
 * только при смене адреса, и после списания на месте показывало на единицу
 * больше, чем шапка (#1701). Запись кошелька теперь сообщает об этом всем,
 * кто показывает число.
 */
export const QUOTA_CHANGED_EVENT = "sup-quota-changed";

export const notifyQuotaChanged = (): void => {
    try {
        window.dispatchEvent(new Event(QUOTA_CHANGED_EVENT));
    } catch {
        /* нет окна — некому и слушать */
    }
};

export async function saveQuestionQuota(
    token: string,
    quota: QuestionQuota,
): Promise<void> {
    try {
        // Режим пакетов: кошелёк пишет только сервер — браузер ничего не сохраняет.
        if (isPlansModeNow()) return;
        await fetch(`${PERSONAL_DATA_WORKER_URL}/data/${QUOTA_PATH}`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(quota),
        });
    } finally {
        // И когда запись не удалась: списание уже произошло на экране,
        // и два разных числа рядом хуже, чем одно устаревшее.
        notifyQuotaChanged();
    }
}

// ---------- server-side spend: a chosen question or search (+50 «Обучение») ----------
//
// Owner 03.10: points for the smart search are «за каждый выбранный вопрос или
// поиск» — as the points description says. A token written off by the browser
// can't be checked by anyone, so the 1-token actions (a chosen answer, a clip
// list, a clip question) ask site_server to write the token off instead:
// POST /api/learning/sup-spend debits the wallet itself, puts the key into
// `viewed` and pays +50 «Обучение» — once per query, at most 10 000 a year
// together with the own question. The price, the one rules consent and the
// moment of the charge stay as they were; a server that doesn't answer falls
// back to today's browser debit (no points), so reading never waits for points.

/** Who spends through the server — see shared/config/supServerSpend. */
export { SUP_SERVER_SPEND, supServerSpendOn } from "@/shared/config/supServerSpend";
export type { SupServerSpend } from "@/shared/config/supServerSpend";

/** lecture — лекция целиком («Мои лекции», плеер; ключ «lecture:<mediaId>», 11.10). */
export type SupSpendKind = "answer" | "clip_list" | "clip_question" | "lecture";

export interface SupSpendResult {
    /** A token was written off by THIS call (false: the query was already paid). */
    charged: boolean;
    /** +50 «Обучение» was written by this call. */
    awarded: boolean;
    points: number;
    reason: string;
    balance: number;
    /** The wallet as the server left it — apply it, don't save it again. */
    quota: QuestionQuota;
    /** Order of the server's wallet writes: of two answers, the higher is newer. */
    seq: number;
}

/** A wallet site_server returned (a spend, «no tokens», an own question's charge). */
export interface ServerWallet {
    quota: QuestionQuota;
    seq: number;
}

/** Read the wallet + its order from a site_server answer body (`data` or the 402 body). */
export const serverWalletOf = (body: unknown): ServerWallet | null => {
    const b = body as { quota?: QuestionQuota | null; seq?: unknown; mode?: unknown; plan?: unknown } | null;
    if (!b) return null;
    // Режим пакетов: сервер отдаёт вид кошелька пакетов (`plan`) — он же идёт
    // в общий вид вкладки (цены и «подписчик» остаются из GET /wallet).
    if (b.mode === "plans" && b.plan) {
        const plan = normalizePlanWallet(b.plan);
        if (!plan || plan.mode !== "plans") return null;
        setPlanWallet(plan, { partial: true });
        return { quota: planQuota(plan), seq: Number(b.seq) || plan.seq || 0 };
    }
    if (!b.quota) return null;
    return { quota: normalizeQuota(b.quota), seq: Number(b.seq) || 0 };
};

/** The signed site session — the only token site_server's learning router reads. */
const siteSessionToken = (): string => {
    try {
        return (localStorage.getItem("site_session_token") || "").trim();
    } catch {
        return "";
    }
};

/** A slow answer must not hold the clip or the answer: past this, the browser debits itself. */
export const SUP_SPEND_TIMEOUT_MS = 8000;

/**
 * Ask site_server to write off one token for a smart-search query.
 * The result — "no_tokens" (402: nothing written, `quota` is the wallet as it
 * is), or null for anything else (no session, network, 4xx/5xx, timeout):
 * the caller then debits in the browser as before, without points.
 */
export async function spendSupQuery(p: {
    kind: SupSpendKind;
    key: string;
    hash?: string;
    text?: string;
    lang?: string;
    searchId?: string | null;
    /** kind=lecture: id лекции (32 hex). */
    mediaId?: string;
    /** kind=lecture: ключ уже оплаченного, из которого лекцию открыли (подборка, ответ). */
    via?: string | null;
}): Promise<
    | SupSpendResult
    | { error: "no_tokens" | "unavailable"; quota: QuestionQuota | null; seq: number }
    | { error: "refused"; code: string; quota: null; seq: number }
    | null
> {
    const token = siteSessionToken();
    if (!token) return null;
    const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
    const timer = ctrl ? setTimeout(() => ctrl.abort(), SUP_SPEND_TIMEOUT_MS) : null;
    try {
        const r = await fetch(
            `${SITE_SERVER_URL}/api/learning/sup-spend?session_token=${encodeURIComponent(token)}`,
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    kind: p.kind,
                    key: p.key,
                    hash: p.hash,
                    text: p.text,
                    lang: p.lang,
                    search_id: p.searchId || undefined,
                    media_id: p.mediaId || undefined,
                    via: p.via || undefined,
                    // Местные сутки скрытого предела (режим пакетов).
                    tz: browserTimeZone() || undefined,
                }),
                signal: ctrl?.signal,
            },
        );
        // 402 — токенов нет / срок поиска вышел (search_closed); 429 — сутки
        // набраны (daily_cap, только у пакетов). Ничего не открыто: страница
        // покажет пустой кошелёк и спросит окно пополнения — в режиме пакетов
        // это окно пакетов или «Продолжим завтра» (features/supPlan).
        if (r.status === 402 || (r.status === 429 && isPlansModeNow())) {
            const w = serverWalletOf(await r.json().catch(() => null));
            return { error: "no_tokens", quota: w ? w.quota : null, seq: w ? w.seq : 0 };
        }
        // Режим пакетов: 503 wallet_unavailable — сервер не смог записать трату (сбой
        // хранилища). Это отказ, а не «сервер молчит»: открытого нет, браузер не открывает
        // бесплатно (сервер в этом случае тоже ничего не выдаёт).
        if (r.status === 503 && isPlansModeNow()) {
            return { error: "unavailable", quota: null, seq: 0 };
        }
        // Лекция: сервер её не знает (404 lecture_not_found) или запрос неверен (400) —
        // не «сервер молчит»: браузер ничего не списывает сам и лекцию не открывает.
        if (p.kind === "lecture" && (r.status === 404 || r.status === 400)) {
            const b = (await r.json().catch(() => null)) as { error?: string } | null;
            return { error: "refused", code: String(b?.error || `http_${r.status}`), quota: null, seq: 0 };
        }
        if (!r.ok) return null;
        const body = (await r.json()) as { data?: Partial<SupSpendResult> & { quota?: QuestionQuota } };
        const d = body?.data;
        // Лекция, а лекции этому человеку не под воротами (владелец 12.10): сервер ничего не
        // списал и кошелька не прислал — не «сервер молчит» (браузер не списывает сам).
        if (p.kind === "lecture" && d?.reason === "gate_off") {
            return { error: "refused", code: "gate_off", quota: null, seq: Number(body?.data?.seq) || 0 };
        }
        const w = serverWalletOf(d);
        if (!d || !w) return null;
        return {
            charged: d.charged === true,
            awarded: d.awarded === true,
            points: Number(d.points) || 0,
            reason: String(d.reason || ""),
            balance: Number(d.balance) || 0,
            quota: w.quota,
            seq: w.seq,
        };
    } catch {
        return null;
    } finally {
        if (timer) clearTimeout(timer);
    }
}
